import type { EditModal } from '@pkg/ui/modal'
import type { ColumnsType } from 'antd/es/table'
import type { ComponentType } from 'react'
import type { IconType } from 'react-icons'
import type { z } from 'zod'

/**
 * Search & Filter configuration
 *
 * Uses plain `string` for sort fields instead of `DeepKeys<TEntity>` to avoid
 * expensive recursive key-path enumeration at every config instantiation site.
 * Type safety for sortable fields comes from `as const` assertions at definition
 * and the `SortableFields<T>` utility type in entityConfig.ts.
 */
export interface SearchConfig<TFilterSchema extends z.ZodType = z.ZodType> {
  /**
   * Available filter schema (for QueryFilterDrawer)
   */
  filterSchema?: TFilterSchema

  /**
   * Available sort fields (use `as const` for type-safe field names)
   */
  sortableFields?: readonly string[]

  /**
   * Default sort field (must be one of sortableFields)
   */
  defaultSortBy?: string

  /**
   * Default sort order
   */
  defaultSortOrder?: 'asc' | 'desc'
}

/**
 * Relation configuration
 * Target entities are automatically inferred from the Zod schema
 *
 * @template TRelationSchema - The Zod schema for this relation
 * @template TTargetEntity - Union of target entity table names (e.g., 'pharmacy' | 'clinic')
 */
export interface RelationConfig<TRelationSchema extends z.ZodType = z.ZodType, TTargetEntity extends string = string> {
  /**
   * Relation table name
   */
  table: string

  /**
   * Relation schema
   * Used to automatically infer target entities from discriminated unions
   */
  schema: TRelationSchema

  /**
   * Singular label (e.g., "Apotheke")
   */
  label: string

  /**
   * Plural label (e.g., "Apotheken")
   */
  labelPlural: string

  /**
   * Icon for relation
   * Can be:
   * - Component constructor from react-icons (e.g., FaStaffSnake)
   * - JSX element (e.g., <FaStaffSnake />)
   * - ReactNode for any other React content
   */
  icon?: ComponentType<never>

  /**
   * Automatically extracted target entities from schema
   * For discriminated unions: all literal types from the union
   * For single entity: the single type
   *
   * Values should match keys from DatabaseSchema (e.g., 'pharmacy', 'clinic')
   */
  readonly targetEntities: readonly TTargetEntity[]

  /**
   * Foreign key field in the relation that references the parent entity
   * If provided, mutate will automatically set this field to the parent entity's ID
   *
   * @example
   * For PatientPharmacyRelation extending PatientRelation with patientId field:
   * foreignKeyField: 'patientId'
   */
  foreignKeyField?: string
}

/**
 * Main Entity Configuration
 * Provides a single source of truth for all entity metadata
 */
export interface EntityConfig<
  TSchema extends z.ZodObject | z.ZodDiscriminatedUnion = z.ZodObject,
  TFilterSchema extends z.ZodType = z.ZodType<object>,
  TDrawerSection extends string = string,
> {
  /**
   * Zod schema for the entity
   */
  schema: TSchema

  /**
   * Singular label (e.g., "Apotheke")
   */
  label: string

  /**
   * Plural label (e.g., "Apotheken")
   */
  labelPlural: string

  /**
   * Icon for entity
   * Can be:
   * - Component constructor from react-icons (e.g., FaStaffSnake)
   * - JSX element (e.g., <FaStaffSnake />)
   * - ReactNode for any other React content
   */
  icon: ComponentType<unknown> | IconType

  /**
   * Routes for entity navigation (optional)
   * Must be valid routes from the route tree
   */
  route?: {
    /**
     * List view route (e.g., '/apotheken')
     */
    list?: string
    /**
     * Edit view route with :id param (e.g., '/apotheken/$id')
     */
    edit?: string
  }

  drawer?: {
    modal: EditModal<
      z.output<TSchema>,
      TDrawerSection,
      z.output<TSchema> extends { id: infer TId extends string } ? TId : string
    >
  }

  /**
   * @deprecated Use `indexColumns` for default columns in list views and `relationSelectionColumns` for relation selection lists instead. This field will be removed in a future release.
   */
  columns?: ColumnsType<z.infer<TSchema>>

  /**
   * Default table columns (antd ColumnsType)
   * Use render functions, not dataIndex
   *
   * These columns are used in standalone entity list views
   */
  indexColumns?: ColumnsType<z.infer<TSchema>>

  /**
   * Relation selection columns (antd ColumnsType)
   * Use render functions, not dataIndex
   *
   * These columns are used for relation entity lists, e.g. in drawers
   */
  relationSelectionColumns: ColumnsType<z.infer<TSchema>>

  /**
   * Search and filter configuration
   */
  search: SearchConfig<TFilterSchema>

  /**
   * Format entity for display
   */
  format: {
    /**
     * Primary display format (e.g., name, title)
     * For patients: includes salutation, title, name, birthdate
     */
    primary: (entity: z.infer<TSchema> | null | undefined) => string | null | undefined

    /**
     * Secondary display detail shown under the primary label (subtitle), e.g. the
     * relation summary card's subtitle line. This is the distinguishing detail, NOT
     * necessarily the address — the address is rendered structurally where needed.
     * Return `undefined` when there is no meaningful subtitle for the entity.
     */
    secondary?: (entity: z.infer<TSchema>) => string | null | undefined
  }
}

/**
 * Type-safe config builder
 * Accepts a function that returns the config object
 * Freezes the config to prevent runtime mutations
 *
 * @template TConfig - The full entity config (inferred from return type)
 *
 * @example
 * export const pharmacyConfig = defineEntityConfig(() => ({
 *   schema: PharmacySchema,
 *   label: t`Apotheke`,
 *   labelPlural: t`Apotheken`,
 *   icon: FaStaffSnake,
 *   relations: {
 *     // ... with targetEntities: ['...'] as const
 *   }
 * }))
 */
export function defineEntityConfig<
  TSchema extends z.ZodObject | z.ZodDiscriminatedUnion,
  TFilterSchema extends z.ZodType = z.ZodObject,
  TDrawerSection extends string = string,
>(
  configFn: () => EntityConfig<TSchema, TFilterSchema, TDrawerSection>,
): Readonly<EntityConfig<TSchema, TFilterSchema, TDrawerSection>> {
  const config = configFn()

  return Object.freeze(config)
}

export type EntityConfigSchema<Keys extends string> = Record<
  Keys,
  EntityConfig<z.ZodObject | z.ZodDiscriminatedUnion, z.ZodType, string>
>
